import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AlertTriangle, Bike, Check, CheckCircle2, Lock, Power, PowerOff, Ruler, X } from "lucide-react";
import { D2C_OPS_STORES_API_URL } from "../../Shared/constant";
import { Badge, toneOf } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Callout, Label } from "./ui/misc";
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import {
  AlertDialog,
  AlertDialogCancelButton,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { cn } from "./ui/utils";
import StateBlock, { Busy } from "./StateBlock";
import d2cPut from "./d2cPut";
import useD2cGet, { errorMessage } from "./useD2cGet";
import { formatDateTime, formatNumber } from "./d2cFormat";
import {
  RADIUS_MAX_KM,
  RADIUS_MIN_KM,
  enrolledLabel,
  radiusError,
  standingBadge,
  storeSwitchBody,
  switchOutcome,
} from "./storeRules";

export const STORES_READ_ONLY_TEXT =
  "You can view stores but not switch them (needs the d2c-switches permission).";

const Field = ({ label, children }) => (
  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
    <span className="tw-text-xs tw-text-muted-foreground">{label}</span>
    <span className="tw-text-sm tw-text-foreground">{children ?? "-"}</span>
  </div>
);

// One readiness check: a green tick, an amber or red cross.
const ReadinessItem = ({ ok, warn, label, value }) => (
  <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-2">
    <span className="tw-flex tw-items-center tw-gap-2.5">
      <span
        className={cn(
          "tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-full",
          ok ? "tw-bg-emerald-100 tw-text-emerald-700" : warn ? "tw-bg-amber-100 tw-text-amber-700" : "tw-bg-red-100 tw-text-red-700",
        )}
      >
        {ok ? <Check className="tw-size-3" aria-hidden="true" /> : <X className="tw-size-3" aria-hidden="true" />}
      </span>
      <span>{label}</span>
      <span className="tw-sr-only">{ok ? "(ok)" : warn ? "(worth a look)" : "(missing)"}</span>
    </span>
    <span className="tw-font-medium tw-tabular-nums">{value}</span>
  </li>
);

const storeTitle = (store) => [store?.name, store?.franchise_code].filter(Boolean).join(" · ");

// One store's D2C status and readiness, with switch on, radius and switch off.
const StoreDrawer = ({ storeId, canEdit, onClose, onChanged }) => {
  const { accessToken } = useSelector((state) => state.adminReducers);
  const detail = useD2cGet(storeId ? `${D2C_OPS_STORES_API_URL}/${storeId}` : null);

  const [fresh, setFresh] = useState(null);
  const [radius, setRadius] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [forbidden, setForbidden] = useState(false);

  // A new store, or a fresh GET, replaces what the last PUT answered.
  useEffect(() => {
    setFresh(null);
  }, [detail.data]);
  useEffect(() => {
    setRadius("");
    setConfirm(null);
    setError(null);
    setNotice("");
  }, [storeId]);

  const store = fresh && fresh.store_id === storeId ? fresh : detail.data;
  const checks = store?.checks || {};
  const readOnly = !canEdit || forbidden;
  const badRadius = radiusError(radius);
  const notReady = store && !store.enabled && (!checks.location || !checks.store_active);

  const ask = (enabled) => {
    if (enabled && badRadius) return;
    const body = storeSwitchBody(enabled, radius);
    const name = storeTitle(store) || "this store";
    let lines;
    if (!enabled) {
      lines = [
        `Switch off ${name}?`,
        "Switching off withdraws this store's open offers; orders it already accepted carry on.",
        "Orders left with no other offer go back to central within a minute. Withdrawn offers do not come back if the store is switched on again.",
      ];
    } else if (store?.enabled) {
      lines = [`Change the serving radius of ${name} to ${body.serving_radius_km} km?`];
    } else {
      lines = [
        `Switch on ${name} for D2C?`,
        "Its dealer users get D2C access and it can receive website bike orders" +
          (body.serving_radius_km ? ` within ${body.serving_radius_km} km.` : " within its serving radius."),
      ];
    }
    setConfirm({ enabled, body, lines });
  };

  const send = async () => {
    if (!confirm) return;
    setSaving(true);
    setError(null);
    const response = await d2cPut(`${D2C_OPS_STORES_API_URL}/${storeId}`, confirm.body, accessToken);
    setSaving(false);
    const enabled = confirm.enabled;
    setConfirm(null);
    if (response?.status === 200) {
      setFresh(response.data);
      setRadius("");
      setNotice(switchOutcome(enabled, response.data));
      detail.reload();
      if (onChanged) onChanged();
      return;
    }
    if (response?.status === 403) {
      setForbidden(true);
      setError(STORES_READ_ONLY_TEXT);
      return;
    }
    setError(errorMessage(response));
  };

  const badge = standingBadge(store);
  const offTone = confirm?.enabled === false;

  return (
    <Sheet open={Boolean(storeId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent>
        <SheetHeader>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <SheetTitle>{store ? storeTitle(store) || "Store" : "Store"}</SheetTitle>
            {store && <Badge tone={toneOf(badge.tone)}>{badge.label}</Badge>}
          </div>
          <SheetDescription>D2C status, readiness and the store switch.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          <StateBlock loading={detail.loading} error={detail.error} onRetry={detail.reload} isEmpty={!store}>
            {store && (
              <>
                <Card>
                  <CardHeader className="tw-pb-2">
                    <CardTitle as="h4">D2C status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 sm:tw-grid-cols-3">
                      <Field label="Status">
                        <Badge tone={toneOf(badge.tone)}>{badge.label}</Badge>
                      </Field>
                      <Field label="Joined via">{enrolledLabel(store)}</Field>
                      <Field label="Joined">{store.joined_at ? formatDateTime(store.joined_at) : "-"}</Field>
                      <Field label="Accepting orders">
                        {store.accepting_orders == null ? "-" : store.accepting_orders ? "Yes" : "No"}
                      </Field>
                      <Field label="Serving radius">
                        {store.serving_radius_km == null ? "-" : `${store.serving_radius_km} km`}
                      </Field>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="tw-pb-0">
                    <CardTitle as="h4">Readiness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="tw-m-0 tw-list-none tw-divide-y tw-p-0 tw-text-sm">
                      <ReadinessItem ok={checks.location} label="Location (latitude and longitude)" value={checks.location ? "Set" : "Missing"} />
                      <ReadinessItem ok={checks.store_active} label="Store active" value={checks.store_active ? "Yes" : "No"} />
                      <ReadinessItem ok={Number(checks.dealer_users) > 0} label="Active dealer users" value={formatNumber(checks.dealer_users)} />
                      <ReadinessItem
                        ok={Number(checks.users_with_access) > 0}
                        warn
                        label="Users with D2C access"
                        value={formatNumber(checks.users_with_access)}
                      />
                      <ReadinessItem ok={Number(checks.free_bikes) > 0} warn label="Free bikes in stock" value={formatNumber(checks.free_bikes)} />
                    </ul>
                    {(checks.free_bikes_by_model || []).length > 0 && (
                      <ul className="tw-m-0 tw-mt-2 tw-list-none tw-space-y-1 tw-rounded-md tw-bg-muted/60 tw-p-3" aria-label="Free bikes by model">
                        {checks.free_bikes_by_model.map((model) => (
                          <li key={model.product_id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-xs">
                            <span className="tw-flex tw-items-center tw-gap-1.5">
                              <Bike className="tw-size-3.5 tw-text-muted-foreground" aria-hidden="true" />
                              {model.name || model.product_id}
                            </span>
                            <span className="tw-font-medium tw-tabular-nums">{formatNumber(model.count)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="tw-pb-2">
                    <CardTitle as="h4">Switch</CardTitle>
                  </CardHeader>
                  <CardContent className="tw-space-y-3">
                    {readOnly && (
                      <Callout tone="warning" icon={Lock}>
                        {STORES_READ_ONLY_TEXT}
                      </Callout>
                    )}
                    {!readOnly && (
                      <>
                        <div className="tw-max-w-xs tw-space-y-1.5">
                          <Label htmlFor="d2c-store-radius" className="tw-text-xs tw-text-muted-foreground">
                            Serving radius in km ({RADIUS_MIN_KM} to {RADIUS_MAX_KM}, optional)
                          </Label>
                          <Input
                            id="d2c-store-radius"
                            type="number"
                            min={RADIUS_MIN_KM}
                            max={RADIUS_MAX_KM}
                            step={1}
                            value={radius}
                            onChange={(e) => setRadius(e.target.value)}
                            placeholder={store.serving_radius_km != null ? String(store.serving_radius_km) : "Store default"}
                            invalid={Boolean(badRadius)}
                            aria-describedby={badRadius ? "d2c-store-radius-error" : undefined}
                          />
                          {badRadius && (
                            <div id="d2c-store-radius-error" className="tw-text-xs tw-text-red-600">
                              {badRadius}
                            </div>
                          )}
                        </div>
                        {notReady && (
                          <div className="tw-text-xs tw-text-red-600">
                            This store cannot be switched on until it has a location and is active.
                          </div>
                        )}
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                          {store.enabled ? (
                            <>
                              <Button
                                variant="outline"
                                onClick={() => ask(true)}
                                disabled={saving || !String(radius).trim() || Boolean(badRadius)}
                              >
                                <Ruler aria-hidden="true" /> Save radius
                              </Button>
                              <Button variant="destructive" onClick={() => ask(false)} disabled={saving}>
                                <PowerOff aria-hidden="true" /> Switch off
                              </Button>
                            </>
                          ) : (
                            <Button onClick={() => ask(true)} disabled={saving || notReady || Boolean(badRadius)}>
                              <Power aria-hidden="true" /> Switch on
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                    {error && (
                      <Callout tone="danger" icon={AlertTriangle} role="alert">
                        {error}
                      </Callout>
                    )}
                    {notice && (
                      <Callout tone="success" icon={CheckCircle2} role="status">
                        {notice}
                      </Callout>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </StateBlock>
        </SheetBody>

        <AlertDialog open={Boolean(confirm)} onOpenChange={(open) => !open && !saving && setConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{offTone ? "Switch off store?" : "Switch store?"}</AlertDialogTitle>
              <AlertDialogDescription>{confirm?.lines?.[0]}</AlertDialogDescription>
            </AlertDialogHeader>
            {(confirm?.lines || []).length > 1 && (
              <ul className="tw-m-0 tw-list-disc tw-space-y-1 tw-pl-5">
                {confirm.lines.slice(1).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            <AlertDialogFooter>
              <AlertDialogCancelButton onClick={() => setConfirm(null)} disabled={saving}>
                Cancel
              </AlertDialogCancelButton>
              <Button variant={offTone ? "destructive" : "default"} onClick={send} disabled={saving}>
                {saving ? <Busy label="Saving…" /> : offTone ? "Switch off" : "Confirm"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};

export default StoreDrawer;
